import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const TOKEN_KEY = "reedos_bcms_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const api = axios.create({
    baseURL: API_BASE,
    headers: { "Content-Type": "application/json" },
    // Iter 66.8 — hung connections must fail fast instead of pinning the
    // UI forever. 30s covers every operational endpoint in the app
    // (including PDF generation + scheduled-snapshot reads).
    timeout: 30000,
});

api.interceptors.request.use((config) => {
    const t = getToken();
    if (t) config.headers.Authorization = `Bearer ${t}`;
    return config;
});

// Iter 66.8 — silent one-shot retry on ERR_NETWORK only.
// Axios emits `err.code === 'ERR_NETWORK'` when the request never reached
// the server (DNS, TCP reset, stale keep-alive, CORS preflight failure,
// etc.). In those cases the server has NOT observed the request, so a
// retry of even a non-idempotent POST cannot create duplicates.
//
// We do NOT retry on:
//   - any HTTP status code (5xx included — server saw it, retry is unsafe)
//   - ECONNABORTED / timeout (request may have been received & processed)
//   - cancelled requests
//
// This kills the "Network Error → click again → works" UX bug the user
// reported on production where a stale keep-alive connection collapses
// the first request and a manual retry opens a fresh socket.
const RETRY_MARKER = "_iter66_8_retried";
api.interceptors.response.use(
    (resp) => resp,
    async (err) => {
        const config = err?.config;
        const isPureNetworkError = err?.code === "ERR_NETWORK" && !err?.response;
        if (isPureNetworkError && config && !config[RETRY_MARKER]) {
            config[RETRY_MARKER] = true;
            // Tiny backoff so we don't hammer the proxy on a true outage.
            await new Promise((r) => setTimeout(r, 500));
            try {
                return await api.request(config);
            } catch (retryErr) {
                err = retryErr;
            }
        }
        if (err?.response?.status === 401) {
            clearToken();
            if (window.location.pathname !== "/login") {
                window.location.href = "/login";
            }
        }
        return Promise.reject(err);
    }
);

export const formatApiError = (err, fallback = "Something went wrong") => {
    const detail = err?.response?.data?.detail;
    if (!detail) {
        // Iter 66.8 — operator-friendly text for the transient network
        // failures the silent retry couldn't recover from. Default axios
        // message "Network Error" is opaque and triggered support tickets
        // when the actual user action was just "tap save once more".
        if (err?.code === "ERR_NETWORK") {
            return "Connection issue — your request didn't reach the server. Please try again in a moment.";
        }
        if (err?.code === "ECONNABORTED" || err?.message === "timeout of 30000ms exceeded") {
            return "The server is taking longer than expected. Please retry shortly.";
        }
        return err?.message || fallback;
    }
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ") || fallback;
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    // Iter 7 · Enterprise Projects — structured authorization denial
    // payloads emitted by access.decide (v1.1 § 10). Shape:
    //   { layer, reason_code, message, action }
    // Surface the human-readable ``message`` (which every layer
    // populates) so toast.error() shows something actionable rather
    // than swallowing the deny to ``fallback``.
    if (detail && typeof detail === "object" && typeof detail.message === "string") {
        return detail.message;
    }
    return fallback;
};

/* C.5 Phase 2.2 — Data Repository logo URL composer.
   Appends a version query parameter derived from the record's
   ``last_updated_at`` (or an explicit override) so a logo replacement
   busts the browser cache the moment the DB row's timestamp changes.
   The backend ignores the query string entirely; same file is served.

   Iter 79.6 (Authorization-Compliance Review) — DEPRECATED for direct
   ``<img src>`` usage. The backend endpoint now requires the
   ``data:logo:download`` permission and a bearer token; a raw URL
   without auth headers will 401. Use ``authedLogoBlob`` below to fetch
   the binary via an authenticated XHR and render via blob URL.
   Kept as an exported helper only for non-rendering call sites
   (debug/inspect/download links) — those paths must explicitly handle
   auth themselves. */
export const dataLogoSrc = (filename, version) => {
    if (!filename) return null;
    const url = `${API_BASE}/data/logos/${filename}`;
    if (!version) return url;
    return `${url}?v=${encodeURIComponent(version)}`;
};

/* Iter 79.6 — Authenticated logo fetcher.
   Fetches the logo binary with the current bearer token attached and
   returns ``{ src, revoke }`` where ``src`` is a session-scoped blob
   URL safe to drop into an <img src=...> attribute, and ``revoke()``
   releases the object URL when the consumer unmounts. Returns ``null``
   if filename is missing or the fetch fails (consumers should render
   the existing placeholder fallback). Cancellation-safe via AbortSignal.

   NOTE: never call this from non-React contexts that don't clean up —
   each call creates a blob URL that persists until ``revoke()`` runs. */
export const authedLogoBlob = async (filename, { signal } = {}) => {
    if (!filename) return null;
    try {
        const resp = await api.get(`/data/logos/${filename}`, {
            responseType: "blob",
            signal,
        });
        const src = URL.createObjectURL(resp.data);
        return { src, revoke: () => URL.revokeObjectURL(src) };
    } catch (err) {
        // 401/403/404 — caller renders placeholder
        return null;
    }
};

/* 2026-02-27 · Authenticated Document Preview + Download.
   The backend endpoint `GET /billing/document/download/{id}` requires a
   `Authorization: Bearer <jwt>` header. Historically the frontend rendered
   plain <a href="…"> anchors for Preview/Download buttons, which the
   browser opens as a fresh navigation with NO auth header attached → the
   backend returns 401 and the new tab shows `{"detail":"Unauthorized"}`.

   These helpers fetch the document via axios (which auto-attaches the
   token via the request interceptor), turn the response into a blob URL,
   and either open the blob in a new tab (preview) or trigger a download
   with the original filename. Both revoke the blob URL after use so the
   browser doesn't leak object URLs across the session.

   Reuse: any surface that used to render an anchor to /document/download/*
   should call these instead. Preserves target=_blank semantics for preview
   while carrying the JWT correctly. */
export const previewDocument = async (documentId, { mime } = {}) => {
    if (!documentId) return;
    const resp = await api.get(`/billing/document/download/${documentId}`, {
        responseType: "blob",
    });
    const contentType = resp.headers?.["content-type"] || mime || "application/pdf";
    const blob = new Blob([resp.data], { type: contentType });
    const url = URL.createObjectURL(blob);
    // Open the tab first, THEN set location — some browsers block a delayed
    // window.open after an async gap unless the click was the direct trigger.
    // We can rely on the immediate-open pattern here because the button
    // handler is the synchronous user gesture that ultimately invokes us.
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (!w) {
        // Popup blocked — fall back to same-tab navigation
        window.location.href = url;
    }
    // Revoke after a beat so the new tab has time to load the resource.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

export const downloadDocument = async (documentId, filename) => {
    if (!documentId) return;
    const resp = await api.get(`/billing/document/download/${documentId}`, {
        responseType: "blob",
    });
    const contentType = resp.headers?.["content-type"] || "application/octet-stream";
    const blob = new Blob([resp.data], { type: contentType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || `document-${documentId}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5_000);
};
